import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  MdBusiness,
  MdEmail,
  MdLocationOn,
  MdPhone,
  MdReceipt,
  MdSettings,
  MdUpload,
  MdEdit,
  MdRefresh,
  MdClose,
} from "react-icons/md";
import { FaCheck } from "react-icons/fa";
import { styled } from "@mui/system";
import api from "../../api/axiosInstance";
import { useSnackbar } from "../../context/SnackbarContext";
import { AuthContext } from "../../context/AuthContext";
import { UserRolesEnum } from "../../typings";
import { Navigate } from "react-router-dom";
import { Button } from "../../components";
import { FlexRow, Spacer } from "../../components";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SystemConfig {
  organization_logo: string;
  organization_name: string;
  organization_address: string;
  organization_email: string;
  organization_phone_number: string;
  additional_text_on_receipt: string;
}

const EMPTY_CONFIG: SystemConfig = {
  organization_logo: "",
  organization_name: "",
  organization_address: "",
  organization_email: "",
  organization_phone_number: "",
  additional_text_on_receipt: "",
};

// ─── Styled components ────────────────────────────────────────────────────────

const PageWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 48px);
  overflow: hidden;
`;

// ContentGrid replaced by MUI Grid below

const SectionCard = styled(motion.div)<{ theme?: any }>`
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #eaecf0;
  padding: 20px 22px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
`;

const FieldLabel = styled(Typography)`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #475467;
  margin-bottom: 4px;
`;

// ─── Logo uploader ─────────────────────────────────────────────────────────────

interface LogoUploaderProps {
  currentUrl: string;
  onUpload: (url: string) => void;
  disabled?: boolean;
  primaryGreen: string;
}

const LogoUploader: React.FC<LogoUploaderProps> = ({
  currentUrl,
  onUpload,
  disabled,
  primaryGreen,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const handleFile = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/svg+xml"];
      if (!allowed.includes(file.type)) {
        showSnackbar("Please upload a valid image (PNG, JPG, WEBP, SVG).", "error");
        return;
      }

      setUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await api.post(
          "/scholarship-applications/documents/",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        onUpload(res.data.data.document_url);
        showSnackbar("Logo uploaded successfully.", "success");
      } catch {
        showSnackbar("Failed to upload logo. Please try again.", "error");
      } finally {
        setUploading(false);
        if (inputRef.current) inputRef.current.value = "";
      }
    },
    [onUpload, showSnackbar]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
      {/* Preview circle */}
      <Box
        sx={{
          position: "relative",
          width: 100,
          height: 100,
          borderRadius: "18px",
          border: `2px dashed ${primaryGreen}55`,
          bgcolor: `${primaryGreen}08`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          cursor: "pointer",
          transition: "border-color 0.2s, box-shadow 0.2s",
          "&:hover": {
            borderColor: primaryGreen,
            boxShadow: `0 0 0 4px ${primaryGreen}12`,
          },
        }}
        onClick={() => !uploading && !disabled && inputRef.current?.click()}
      >
        {uploading ? (
          <CircularProgress size={28} thickness={4} sx={{ color: primaryGreen }} />
        ) : currentUrl ? (
          <>
            <img
              src={currentUrl}
              alt="Organization logo"
              style={{ width: "100%", height: "100%", objectFit: "contain", padding: 8 }}
            />
            {/* Hover edit overlay */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(0,0,0,0.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0,
                transition: "opacity 0.2s",
                "&:hover": { opacity: 1 },
              }}
            >
              <MdEdit size={22} color="white" />
            </Box>
          </>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
            <MdUpload size={24} color={`${primaryGreen}80`} />
            <Typography sx={{ fontSize: "0.65rem", color: `${primaryGreen}80` }}>
              Upload
            </Typography>
          </Box>
        )}
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFile}
      />

      <Typography sx={{ fontSize: "0.7rem", color: "#98a2b3", textAlign: "center" }}>
        PNG, JPG, SVG
      </Typography>

      {currentUrl && (
        <Tooltip title="Remove logo">
          <Box
            onClick={() => !disabled && onUpload("")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.4,
              cursor: "pointer",
              color: "#F04438",
              fontSize: "0.72rem",
              fontWeight: 600,
              "&:hover": { opacity: 0.75 },
            }}
          >
            <MdClose size={12} />
            Remove
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

// ─── Field helper ─────────────────────────────────────────────────────────────

const makeFieldSx = (primaryGreen: string) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#FAFAFA",
    fontSize: "0.85rem",
    "& fieldset": { borderColor: "#EAECF0", borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: primaryGreen + "88" },
    "&.Mui-focused fieldset": { borderColor: primaryGreen, borderWidth: "2px" },
    "& input, & textarea": { color: "#101828", fontWeight: 500 },
  },
  "& label": { fontSize: "0.82rem", color: "#667085" },
  "& label.Mui-focused": { color: primaryGreen },
  "& .MuiInputAdornment-root svg": { color: primaryGreen + "aa" },
});

// ─── Main component ────────────────────────────────────────────────────────────

const Settings: React.FC = () => {
  const theme = useTheme();
  const auth = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();
  const colors = theme.palette.colors;
  const primaryGreen = theme.palette.primary.main; // #2C8B63

  // ─ State (all hooks before any return) ─
  const [fetchLoading, setFetchLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [serverConfig, setServerConfig] = useState<SystemConfig>(EMPTY_CONFIG);
  const [form, setForm] = useState<SystemConfig>(EMPTY_CONFIG);

  const normalize = useCallback((data: any): SystemConfig => ({
    organization_logo: data?.organization_logo ?? "",
    organization_name: data?.organization_name ?? "",
    organization_address: data?.organization_address ?? "",
    organization_email: data?.organization_email ?? "",
    organization_phone_number: data?.organization_phone_number ?? "",
    additional_text_on_receipt: data?.additional_text_on_receipt ?? "",
  }), []);

  const fetchConfig = useCallback(async () => {
    setFetchLoading(true);
    setFetchError(null);
    try {
      const res = await api.get("/config/");
      const n = normalize(res.data?.data);
      setServerConfig(n);
      setForm(n);
    } catch {
      setFetchError("Could not load configuration.");
    } finally {
      setFetchLoading(false);
    }
  }, [normalize]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleChange = useCallback(
    (field: keyof SystemConfig) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
      },
    []
  );

  const handleLogoChange = useCallback((url: string) => {
    setForm((prev) => ({ ...prev, organization_logo: url }));
  }, []);

  const handleSave = useCallback(async () => {
    setSaveLoading(true);
    setSaved(false);
    try {
      const res = await api.post("/config/", { ...form });
      const n = normalize(res.data?.data ?? form);
      setServerConfig(n);
      setForm(n);
      setSaved(true);
      showSnackbar("Configuration saved successfully.", "success");
      setTimeout(() => setSaved(false), 3000);
    } catch {
      showSnackbar("Failed to save. Please try again.", "error");
    } finally {
      setSaveLoading(false);
    }
  }, [form, normalize, showSnackbar]);

  const handleReset = useCallback(() => {
    setForm(serverConfig);
    showSnackbar("Changes discarded.", "info");
  }, [serverConfig, showSnackbar]);

  // ─ Guard (after all hooks) ─
  if (!auth || auth.role !== UserRolesEnum.SUPER_ADMIN) {
    return <Navigate to="/" replace />;
  }

  const isDirty = JSON.stringify(form) !== JSON.stringify(serverConfig);
  const fieldSx = makeFieldSx(primaryGreen);

  // ─ Loading skeleton ─
  if (fetchLoading) {
    return (
      <PageWrapper>
        {/* Header skeleton */}
        <FlexRow sx={{ alignItems: "center", pb: "15px" }}>
          <Skeleton width={240} height={40} />
          <Box sx={{ ml: "auto" }}>
            <Skeleton variant="rounded" width={120} height={36} sx={{ borderRadius: "5px" }} />
          </Box>
        </FlexRow>
        <Grid container spacing={2} sx={{ flex: 1, mt: 0 }}>
          <Grid item xs={5}>
            <Skeleton variant="rounded" height={280} sx={{ borderRadius: "16px" }} />
          </Grid>
          <Grid item xs={7}>
            <Skeleton variant="rounded" height={280} sx={{ borderRadius: "16px" }} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant="rounded" height={140} sx={{ borderRadius: "16px" }} />
          </Grid>
        </Grid>
      </PageWrapper>
    );
  }

  // ─ Error state ─
  if (fetchError) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", gap: 2 }}>
        <MdSettings size={56} color={colors.gray["200"]} />
        <Typography sx={{ color: colors.gray["700"], fontWeight: 700 }}>Failed to load settings</Typography>
        <Typography sx={{ color: colors.gray["500"], fontSize: "0.85rem" }}>{fetchError}</Typography>
        <Box
          component="button"
          onClick={fetchConfig}
          sx={{
            mt: 1, px: 3, py: 1, borderRadius: "8px",
            bgcolor: primaryGreen, color: "white", border: "none", cursor: "pointer",
            fontWeight: 700, fontSize: "0.85rem",
            "&:hover": { bgcolor: colors.green["700"] },
          }}
        >
          Try Again
        </Box>
      </Box>
    );
  }

  // ─ Render ─
  return (
    <PageWrapper>
      {/* ── Page header ──────────────────────────────────────────────────── */}
      <FlexRow
        sx={{
          alignItems: "center",
          pb: "15px",
          borderBottom: `1px solid ${colors.gray["200"]}`,
          mb: "16px",
          flexWrap: "nowrap",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {/* <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              bgcolor: `${primaryGreen}15`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MdSettings size={20} color={primaryGreen} />
          </Box> */}
          <Box>
            <Typography
              variant="h4"
              component="div"
              sx={{ fontWeight: "bold", color: "primary.main", lineHeight: 1.1 }}
            >
              System Settings
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: colors.gray["500"], mt: 0.2 }}>
              Manage your organization's global configuration
            </Typography>
          </Box>
        </Box>

        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={fetchConfig}
              sx={{
                color: colors.gray["400"],
                "&:hover": { color: primaryGreen, bgcolor: `${primaryGreen}10` },
              }}
            >
              <MdRefresh size={18} />
            </IconButton>
          </Tooltip>

          {isDirty && (
            <Button
              buttonColor={colors.gray["200"] as any}
              textColor={colors.gray["700"] as any}
              onClick={handleReset}
              disabled={saveLoading}
              sx={{
                minHeight: "32px",
                padding: "0px 14px",
                borderRadius: "5px",
                fontSize: "0.8rem",
                backgroundColor: "#FFFFFF",
                border: `1px solid ${colors.gray["300"]}`,
                color: colors.gray["700"],
                "&:hover": { backgroundColor: colors.gray["50"] },
              }}
            >
              Discard
            </Button>
          )}

          <Button
            buttonColor={colors.yellow["300"] as any}
            onClick={handleSave}
            disabled={saveLoading}
            sx={{
              minHeight: "32px",
              padding: "0px 18px",
              borderRadius: "5px",
              fontSize: "0.82rem",
              fontWeight: 700,
              color: colors.gray["900"],
            }}
          >
            {saveLoading ? (
              <CircularProgress size={14} thickness={4} sx={{ color: colors.gray["800"] }} />
            ) : saved ? (
              <>
                <FaCheck size={12} />
                <Spacer horizontal={0.4} />
                Saved
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Box>
      </FlexRow>

      {/* ── Grid body ───────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>

        {/* ── Row 1: Branding (5) + Contact (7) ─────────────────────────── */}

        {/* Branding */}
        <Grid item xs={5}>
          <SectionCard
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            style={{ height: "100%" }}
          >
            {/* Section title */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  bgcolor: `${primaryGreen}14`,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <MdBusiness size={16} color={primaryGreen} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: "#101828", fontSize: "1.2rem", lineHeight: 1.2 }}>
                  Branding
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", color: colors.gray["500"], mt: 0.1 }}>
                  Logo &amp; organization name
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <LogoUploader
                currentUrl={form.organization_logo}
                onUpload={handleLogoChange}
                disabled={saveLoading}
                primaryGreen={primaryGreen}
              />

              <Box sx={{ width: "100%", mt: 1 }}>
                <FieldLabel>Organization Name</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  value={form.organization_name}
                  onChange={handleChange("organization_name")}
                  disabled={saveLoading}
                  placeholder="e.g. Holding Hands Foundation"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdBusiness size={15} />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />
              </Box>
            </Box>
          </SectionCard>
        </Grid>

        {/* Contact Information */}
        <Grid item xs={7}>
          <SectionCard
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.06, ease: "easeOut" }}
            style={{ height: "100%" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  bgcolor: `${primaryGreen}14`,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <MdEmail size={16} color={primaryGreen} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: "#101828", fontSize: "1.2rem", lineHeight: 1.2 }}>
                  Contact Information
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", color: colors.gray["500"], mt: 0.1 }}>
                  Used in receipts &amp; official documents
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <FieldLabel>Email</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  value={form.organization_email}
                  onChange={handleChange("organization_email")}
                  disabled={saveLoading}
                  placeholder="info@organization.com"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdEmail size={15} />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FieldLabel>Phone Number</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  value={form.organization_phone_number}
                  onChange={handleChange("organization_phone_number")}
                  disabled={saveLoading}
                  placeholder="+92 300 0000000"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdPhone size={15} />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />
              </Grid>

              <Grid item xs={12}>
                <FieldLabel>Address</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  value={form.organization_address}
                  onChange={handleChange("organization_address")}
                  disabled={saveLoading}
                  placeholder="Street, City, Country"
                  multiline
                  rows={4}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ mt: "-60px" }}>
                        <MdLocationOn size={15} />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        {/* ── Row 2: Receipt Settings (full width) ─────────────────────── */}
        <Grid item xs={12}>
          <SectionCard
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.12, ease: "easeOut" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  bgcolor: `${primaryGreen}14`,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <MdReceipt size={16} color={primaryGreen} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: "#101828", fontSize: "1.2rem", lineHeight: 1.2 }}>
                  Receipt Settings
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", color: colors.gray["500"], mt: 0.1 }}>
                  Customise the text on printed receipts
                </Typography>
              </Box>
            </Box>

            <Box>
              <FieldLabel>Additional Text on Receipt</FieldLabel>
              <TextField
                fullWidth
                size="small"
                value={form.additional_text_on_receipt}
                onChange={handleChange("additional_text_on_receipt")}
                disabled={saveLoading}
                placeholder="e.g. Thank you for your support. This is an official receipt."
                multiline
                rows={5}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mt: "10px" }}>
                      <MdReceipt size={15} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  ...fieldSx,
                  "& .MuiOutlinedInput-root": {
                    ...fieldSx["& .MuiOutlinedInput-root"],
                    alignItems: "flex-start",
                  },
                }}
              />
            </Box>
          </SectionCard>
        </Grid>

      </Grid>
    </PageWrapper>
  );
};

export default Settings;
